import { getToken } from './auth'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface User { id: string; email: string; name: string; role: string }

export interface Event {
  id: string; name: string; description?: string
  date: string; startTime?: string; location?: string; status: string
  countAttendees?: boolean
  requiresPayment?: boolean
}
export interface ContestantConfirmation {
  id: string; name: string; cosplayerName: string; characterName: string; email: string
  contestantNumber?: number; registrationStatus: string; registeredAt: string
  competitionName: string; eventName: string; eventDate: string; eventLocation?: string
}
export interface AdminEvent extends Event {
  competitionCount: number; createdAt: string; updatedAt: string
}
export interface ProgramSegment {
  id: string; name: string; type?: string
  startTime?: string; endTime?: string; orderIndex: number; status: string
}
export interface Competition {
  id: string; name: string; category?: string; status: string
  eventId?: string; eventName?: string; eventDate?: string
  currentContestantNumber: number
}
export interface AdminCompetition extends Competition {
  contestantCount: number; judgeCount: number
}
export interface AdminEventDetail extends AdminEvent {
  programSegments: ProgramSegment[]
  competitions: (Competition & { contestantCount: number })[]
}
export interface JudgingCriterion {
  id: string; name: string; weight: number; maxScore: number
  description?: string; orderIndex: number
}
export interface AdminContestant {
  id: string; name: string; cosplayerName: string; email: string
  characterName: string; registrationStatus: string
  competitionId: string; eventId: string; competitionName?: string; eventName?: string
  contestantNumber?: number; registeredAt: string
}
export interface ContestantAttachment {
  id: string; fileUrl: string; fileName?: string; fileType?: string
  fileSize?: number; uploadedAt: string
}
export interface ContestantDetail extends AdminContestant {
  facebookUrl?: string; instagramUrl?: string; twitterUrl?: string
  characterDescription?: string; characterUrl?: string
  attachments: ContestantAttachment[]
}
export interface CompetitionContestant {
  id: string; name: string; cosplayerName: string; email: string
  characterName: string; registrationStatus: string; contestantNumber?: number
  scoreCount?: number
}
export interface CompetitionJudge {
  id: string; name: string; email: string; assignmentId: string; assignmentStatus: string
}
export interface AdminCompetitionDetail {
  id: string; name: string; category?: string; status: string; description?: string
  eventId: string; eventName: string; eventDate: string
  currentContestantNumber: number; contestantCount: number; judgeCount: number
  judgingCriteria: JudgingCriterion[]
  contestants: CompetitionContestant[]
  judges: CompetitionJudge[]
}
export interface JudgeUser {
  id: string; email: string; name: string; role: string
  createdAt: string; competitionCount?: number
}
export interface Analytics {
  totals: { events: number; competitions: number; contestants: number; judges: number }
  eventsByStatus: { status: string; count: number }[]
  competitionsByStatus: { status: string; count: number }[]
  registrationsByStatus: { status: string; count: number }[]
  recentRegistrations: {
    id: string; name: string; cosplayerName: string; characterName: string
    registrationStatus: string; registeredAt: string
    competitionName: string; eventName: string
  }[]
  topCompetitions: {
    id: string; name: string; status: string; category?: string
    eventName: string; contestantCount: number
  }[]
}
export interface JudgeCompetition {
  id: string; name: string; category?: string; status: string
  eventName: string; eventDate: string; assignmentStatus: string
}
export interface JudgeContestant {
  id: string; name: string; cosplayerName: string; characterName: string
  contestantNumber: number; registrationStatus: string
  myScores?: { criteriaId: string; score: number; notes?: string; status: string }[]
}
export interface ScoreEntry { criteria_id: string; score: number; notes?: string }
export interface ContestantSummary {
  id: string; name: string; cosplayerName: string; characterName: string
  contestantNumber: number; totalScore: number; rank: number | null
  scores: { criteriaId: string; score: number }[]
}
export interface JudgeSummary {
  competition_id: string
  summary: ContestantSummary[]
  criteria: JudgingCriterion[]
}
export interface PublicCompetition extends Competition {
  judgingCriteria?: JudgingCriterion[]
  eventRequiresPayment?: boolean
}

// ── Registration Types ────────────────────────────────────────────────────

export type FormFieldType = 'text' | 'textarea' | 'email' | 'phone' | 'number' | 'select' | 'checkbox' | 'file'

export interface FormField {
  id: string
  label: string
  type: FormFieldType
  required?: boolean
  options?: string[]
  placeholder?: string
}

export interface RegistrationType {
  id: string
  eventId: string
  name: string
  description?: string
  fields: FormField[]
  requiresPayment: boolean
  isActive: boolean
  maxSlots?: number | null
  orderIndex: number
  registrationCount?: number
  paymentNote?: string | null
  paymentAttachmentUrl?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface EventRegistration {
  id: string
  registrationTypeId: string
  eventId: string
  data: Record<string, unknown>
  email?: string
  status: 'pending' | 'payment_pending' | 'confirmed' | 'rejected'
  paymentProofUrl?: string
  paymentProofName?: string
  paymentVerifiedAt?: string
  paymentRejectedAt?: string
  submittedAt: string
  updatedAt?: string
  registrationTypeName?: string
  eventName?: string
  eventDate?: string
  eventLocation?: string
  typeFields?: FormField[]
}

export interface PaymentSettings {
  paymentNote: string
  paymentQrUrl?: string
  accountName?: string
}

export interface EmailSettings {
  enabled: boolean
  smtpHost: string
  smtpPort: number
  smtpUser: string
  smtpPass: string
  fromName: string
  fromEmail: string
}

export interface PaymentRecord {
  id: string
  contestantId: string
  contestantName: string
  cosplayerName: string
  email: string
  competitionName: string
  eventName: string
  proofUrl: string
  proofFileName?: string
  status: 'pending' | 'verified' | 'rejected'
  submittedAt: string
  verifiedAt?: string
  rejectedAt?: string
}

// ── Auth ───────────────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string, role: 'admin' | 'judge') =>
    request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, role }),
    }),
}

// ── Events ─────────────────────────────────────────────────────────────────

export const eventsApi = {
  listPublic: () => request<Event[]>('/api/events'),
  getPublic: (id: string) => request<Event>(`/api/events/${id}`),
  listAdmin: () => request<AdminEvent[]>('/api/admin/events'),
  get: (id: string) => request<AdminEventDetail>(`/api/admin/events/${id}`),
  create: (data: { name: string; description?: string; date: string; start_time?: string; location?: string; status?: string; count_attendees?: boolean; requires_payment?: boolean }) =>
    request<AdminEvent>('/api/admin/events', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    request<AdminEvent>(`/api/admin/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/api/admin/events/${id}`, { method: 'DELETE' }),
  setStatus: (id: string, status: string) =>
    request<AdminEvent>(`/api/admin/events/${id}/status`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    }),
}

// ── Competitions ───────────────────────────────────────────────────────────

export const competitionsApi = {
  listPublic: () => request<PublicCompetition[]>('/api/competitions'),
  listAdmin: () => request<AdminCompetition[]>('/api/admin/competitions'),
  get: (id: string) => request<AdminCompetitionDetail>(`/api/admin/competitions/${id}`),
  create: (data: { event_id: string; name: string; category?: string; judging_model?: string }) =>
    request<Competition>('/api/admin/competitions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    request<Competition>(`/api/admin/competitions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/api/admin/competitions/${id}`, { method: 'DELETE' }),
  setStatus: (id: string, status: string) =>
    request<Competition>(`/api/admin/competitions/${id}/status`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    }),
}

// ── Criteria ───────────────────────────────────────────────────────────────

export const criteriaApi = {
  add: (competitionId: string, data: { name: string; weight: number; max_score?: number; description?: string; order_index?: number }) =>
    request<JudgingCriterion>(`/api/admin/competitions/${competitionId}/criteria`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (criteriaId: string, data: Partial<{ name: string; weight: number; max_score: number; description: string; order_index: number }>) =>
    request<JudgingCriterion>(`/api/admin/criteria/${criteriaId}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),
  delete: (criteriaId: string) =>
    request<{ message: string }>(`/api/admin/criteria/${criteriaId}`, { method: 'DELETE' }),
}

// ── Contestants ────────────────────────────────────────────────────────────

export const contestantsApi = {
  listAdmin: () => request<AdminContestant[]>('/api/admin/contestants'),
  get: (id: string) => request<ContestantDetail>(`/api/admin/contestants/${id}`),
  update: (id: string, data: Record<string, unknown>) =>
    request<AdminContestant>(`/api/admin/contestants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadAttachments: async (id: string, files: File[]): Promise<ContestantAttachment[]> => {
    const token = getToken()
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    const res = await fetch(`${API_URL}/api/contestants/${id}/attachments`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
  register: (data: {
    competition_id: string; event_id: string; name: string; cosplayer_name: string
    email: string; facebook_url?: string; instagram_url?: string; twitter_url?: string
    character_name: string; character_url?: string; character_description?: string
  }) =>
    request<AdminContestant>('/api/register', { method: 'POST', body: JSON.stringify(data) }),
  updateStatus: (id: string, registrationStatus: string) =>
    request<AdminContestant>(`/api/admin/contestants/${id}/status`, {
      method: 'PATCH', body: JSON.stringify({ registration_status: registrationStatus }),
    }),
  delete: (id: string) =>
    request<{ message: string }>(`/api/admin/contestants/${id}`, { method: 'DELETE' }),
  getConfirmation: (id: string) =>
    request<ContestantConfirmation>(`/api/contestants/${id}/confirmation`),
}

// ── Slider ─────────────────────────────────────────────────────────────────

export interface SliderSlide {
  id: string
  title: string
  subtitle: string
  imageUrl?: string
  ctaText: string
  ctaLink: string
  backgroundColor: string
  orderIndex: number
}

export const sliderApi = {
  listPublic: () => request<SliderSlide[]>('/api/slider'),
  listAdmin: () => request<SliderSlide[]>('/api/admin/slider'),
  create: (data: Omit<SliderSlide, 'id'>) =>
    request<SliderSlide>('/api/admin/slider', {
      method: 'POST',
      body: JSON.stringify({
        title: data.title, subtitle: data.subtitle, image_url: data.imageUrl ?? null,
        cta_text: data.ctaText, cta_link: data.ctaLink,
        background_color: data.backgroundColor, order_index: data.orderIndex,
      }),
    }),
  update: (id: string, data: Omit<SliderSlide, 'id'>) =>
    request<SliderSlide>(`/api/admin/slider/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        title: data.title, subtitle: data.subtitle, image_url: data.imageUrl ?? null,
        cta_text: data.ctaText, cta_link: data.ctaLink,
        background_color: data.backgroundColor, order_index: data.orderIndex,
      }),
    }),
  delete: (id: string) =>
    request<{ message: string }>(`/api/admin/slider/${id}`, { method: 'DELETE' }),
  uploadImage: async (file: File): Promise<{ url: string }> => {
    const token = getToken()
    const form = new FormData()
    form.append('image', file)
    const res = await fetch(`${API_URL}/api/admin/slider-images`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
}

// ── Judges ─────────────────────────────────────────────────────────────────

export const judgesApi = {
  list: () => request<JudgeUser[]>('/api/admin/judges'),
  create: (data: { email: string; name: string; password: string }) =>
    request<JudgeUser>('/api/admin/judges', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ message: string }>(`/api/admin/judges/${id}`, { method: 'DELETE' }),
  assign: (competitionId: string, userId: string) =>
    request<{ id: string; userId: string; competitionId: string; status: string }>(
      `/api/admin/competitions/${competitionId}/judges`,
      { method: 'POST', body: JSON.stringify({ user_id: userId }) }
    ),
  unassign: (assignmentId: string) =>
    request<{ message: string }>(`/api/admin/judge-assignments/${assignmentId}`, { method: 'DELETE' }),
}

// ── Analytics ──────────────────────────────────────────────────────────────

export const analyticsApi = {
  get: () => request<Analytics>('/api/admin/analytics'),
}

// ── Segments ───────────────────────────────────────────────────────────────

export const segmentsApi = {
  create: (eventId: string, data: { name: string; type?: string; start_time?: string; end_time?: string; order_index?: number }) =>
    request<ProgramSegment>(`/api/admin/events/${eventId}/segments`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (segmentId: string, data: { name?: string; type?: string; start_time?: string; end_time?: string; order_index?: number }) =>
    request<ProgramSegment>(`/api/admin/segments/${segmentId}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),
  delete: (segmentId: string) =>
    request<{ message: string }>(`/api/admin/segments/${segmentId}`, { method: 'DELETE' }),
}

// ── Payment Settings ───────────────────────────────────────────────────────

export const paymentSettingsApi = {
  getPublic: () => request<PaymentSettings>('/api/settings/payment'),
  getAdmin: () => request<PaymentSettings>('/api/admin/settings/payment'),
  update: (data: Partial<PaymentSettings>) =>
    request<PaymentSettings>('/api/admin/settings/payment', {
      method: 'PUT', body: JSON.stringify(data),
    }),
  uploadQr: async (file: File): Promise<{ url: string }> => {
    const token = getToken()
    const form = new FormData()
    form.append('image', file)
    const res = await fetch(`${API_URL}/api/admin/settings/payment-qr`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
}

// ── Email Settings ─────────────────────────────────────────────────────────

export const emailSettingsApi = {
  get: () => request<EmailSettings>('/api/admin/settings/email'),
  update: (data: Partial<EmailSettings>) =>
    request<EmailSettings>('/api/admin/settings/email', {
      method: 'PUT', body: JSON.stringify(data),
    }),
  test: (toEmail: string) =>
    request<{ message: string }>('/api/admin/settings/email/test', {
      method: 'POST', body: JSON.stringify({ to_email: toEmail }),
    }),
}

// ── Payments ───────────────────────────────────────────────────────────────

export const paymentsApi = {
  submitProof: async (contestantId: string, file: File): Promise<PaymentRecord> => {
    const form = new FormData()
    form.append('proof', file)
    form.append('contestant_id', contestantId)
    const res = await fetch(`${API_URL}/api/payments/submit`, {
      method: 'POST',
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
  listAdmin: () => request<PaymentRecord[]>('/api/admin/payments'),
  verify: (id: string) =>
    request<PaymentRecord>(`/api/admin/payments/${id}/verify`, { method: 'PATCH' }),
  reject: (id: string) =>
    request<PaymentRecord>(`/api/admin/payments/${id}/reject`, { method: 'PATCH' }),
  sendConfirmationEmail: (id: string) =>
    request<{ message: string }>(`/api/admin/payments/${id}/send-email`, { method: 'POST' }),
}

// ── Registration Types API ─────────────────────────────────────────────────

export const registrationTypesApi = {
  listPublic: (eventId: string) =>
    request<RegistrationType[]>(`/api/events/${eventId}/registration-types`),
  listAdmin: (eventId: string) =>
    request<RegistrationType[]>(`/api/admin/events/${eventId}/registration-types`),
  get: (typeId: string) =>
    request<RegistrationType>(`/api/admin/registration-types/${typeId}`),
  create: (eventId: string, data: Omit<RegistrationType, 'id' | 'eventId' | 'registrationCount' | 'createdAt' | 'updatedAt'>) =>
    request<RegistrationType>(`/api/admin/events/${eventId}/registration-types`, {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        fields: data.fields,
        requires_payment: data.requiresPayment,
        is_active: data.isActive,
        max_slots: data.maxSlots ?? null,
        order_index: data.orderIndex,
        payment_note: data.paymentNote ?? null,
      }),
    }),
  update: (typeId: string, data: Partial<Omit<RegistrationType, 'id' | 'eventId' | 'registrationCount' | 'createdAt' | 'updatedAt'>>) =>
    request<RegistrationType>(`/api/admin/registration-types/${typeId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...(data.name !== undefined && { name: data.name }),
        ...(data.description !== undefined && { description: data.description }),
        ...(data.fields !== undefined && { fields: data.fields }),
        ...(data.requiresPayment !== undefined && { requires_payment: data.requiresPayment }),
        ...(data.isActive !== undefined && { is_active: data.isActive }),
        ...(data.maxSlots !== undefined && { max_slots: data.maxSlots }),
        ...(data.orderIndex !== undefined && { order_index: data.orderIndex }),
        ...(data.paymentNote !== undefined && { payment_note: data.paymentNote }),
      }),
    }),
  delete: (typeId: string) =>
    request<{ message: string }>(`/api/admin/registration-types/${typeId}`, { method: 'DELETE' }),
  uploadPaymentAttachment: async (typeId: string, file: File): Promise<RegistrationType> => {
    const token = getToken()
    const form = new FormData()
    form.append('attachment', file)
    const res = await fetch(`${API_URL}/api/admin/registration-types/${typeId}/payment-attachment`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
  deletePaymentAttachment: (typeId: string) =>
    request<RegistrationType>(`/api/admin/registration-types/${typeId}/payment-attachment`, { method: 'DELETE' }),
}

// ── Event Registrations API ────────────────────────────────────────────────

export const eventRegistrationsApi = {
  submit: async (eventId: string, registrationTypeId: string, data: Record<string, unknown>, email?: string, paymentProof?: File, files?: Record<string, File>): Promise<EventRegistration> => {
    const form = new FormData()
    form.append('registration_type_id', registrationTypeId)
    form.append('data', JSON.stringify(data))
    if (email) form.append('email', email)
    if (paymentProof) form.append('payment_proof', paymentProof)
    if (files) {
      for (const [fieldId, file] of Object.entries(files)) {
        form.append(`file_field_${fieldId}`, file)
      }
    }
    const res = await fetch(`${API_URL}/api/events/${eventId}/registrations`, {
      method: 'POST',
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
  submitPaymentProof: async (eventId: string, regId: string, file: File): Promise<EventRegistration> => {
    const form = new FormData()
    form.append('payment_proof', file)
    const res = await fetch(`${API_URL}/api/events/${eventId}/registrations/${regId}/payment`, {
      method: 'POST',
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  },
  getPublic: (regId: string) => request<EventRegistration>(`/api/registrations/${regId}`),
  listAllAdmin: () =>
    request<EventRegistration[]>('/api/admin/registrations'),
  listAdmin: (eventId: string) =>
    request<EventRegistration[]>(`/api/admin/events/${eventId}/registrations`),
  getAdmin: (regId: string) => request<EventRegistration>(`/api/admin/registrations/${regId}`),
  verify: (regId: string) =>
    request<EventRegistration>(`/api/admin/registrations/${regId}/verify`, { method: 'PATCH' }),
  reject: (regId: string) =>
    request<EventRegistration>(`/api/admin/registrations/${regId}/reject`, { method: 'PATCH' }),
  setStatus: (regId: string, status: string) =>
    request<EventRegistration>(`/api/admin/registrations/${regId}/status`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    }),
}

// ── Judge Scoring ──────────────────────────────────────────────────────────

export const scoresApi = {
  myCompetitions: () => request<JudgeCompetition[]>('/api/judge/competitions'),
  contestants: (competitionId: string) =>
    request<JudgeContestant[]>(`/api/judge/competitions/${competitionId}/contestants`),
  submit: (data: { competition_id: string; contestant_id: string; scores: ScoreEntry[] }) =>
    request<{ message: string; scores: unknown[] }>('/api/judge/scores', {
      method: 'POST', body: JSON.stringify(data),
    }),
  summary: (competitionId: string) =>
    request<JudgeSummary>(`/api/judge/competitions/${competitionId}/summary`),
  criteria: (competitionId: string) =>
    request<JudgingCriterion[]>(`/api/admin/competitions/${competitionId}/criteria`),
}
